import React, { useEffect, useMemo, useState } from "react";
import { Table, Input, Select, Button, Checkbox, Divider, Space, Typography, Modal, Tag, Segmented, Descriptions, Tooltip, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, DeleteOutlined, EditOutlined, FolderAddOutlined, CloseCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import NumberInput from "./NumberInput";
import TextInput from "./TextInput";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ExpandCollapseAllButton from "./ExpandCollapseAllButton";
import { SummaryKeyRawFields } from "./BcCalculationDetails";
import {
  COLORS,
  UNIT_MAP,
  formatUnit,
  RESOURCE_CODE_OPERATOR_OPTIONS,
  MAX_SECTION_NESTING_DEPTH,
  MAX_SECTION_NESTING_DEPTH_MESSAGE,
} from "../../utils/constants";
import { useDevMode } from "@/contexts/DevModeContext";
import {
  getPlotMatrixStageNames,
  findPlotMatrixColumnsForStage,
  getPlotMatrixSubStageLabel,
  isGarageEligibleValuationStage,
} from "../../utils/dynamicBillSummaryPlotMatrix";
import {
  getRunMatrixMappingSectionLabels,
  findRunMatrixMappingColumnsForSection,
  type RunMatrixMappingSectionKey,
} from "../../utils/dynamicBillSummaryRunMatrix";
import {
  nodeToLineItem,
  computeDynamicBcCalculation,
  computeDynamicBillSummaryNodeAmount,
  computeDynamicBillSummaryNodeRate,
  type DynamicBillSummaryLineItem,
} from "../../utils/dynamicBillSummarySerialization";
import type { ItemLibraryItem } from "../../api/itemLibrary";
import type { PlotMatrixCategoryColumn } from "../../utils/plotMatrixCategoryGenerator";
import type {
  DynamicBillSummaryNode,
  DynamicBillSummarySectionNode,
  DynamicBillSummaryItemNode,
  DynamicBillSummaryTab,
  DynamicBillSummaryPlotMapping,
  DynamicBillSummaryRunMatrixMapping,
  DynamicBillSummaryResourceCodeEntry,
} from "../../types/tender.types";

const { Text, Title } = Typography;

let idCounter = 0;
const genNodeId = () => `n${Date.now()}_${idCounter++}`;

const isSection = (
  node: DynamicBillSummaryNode,
): node is DynamicBillSummarySectionNode => node.type === "section";

const formatCurrency = (value: number) =>
  `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const mapNodes = (
  nodes: DynamicBillSummaryNode[],
  id: string,
  fn: (node: DynamicBillSummaryNode) => DynamicBillSummaryNode,
): DynamicBillSummaryNode[] =>
  nodes.map((node) => {
    if (node.id === id) return fn(node);
    if (isSection(node)) return { ...node, children: mapNodes(node.children, id, fn) };
    return node;
  });

const removeNodeById = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode[] =>
  nodes
    .filter((node) => node.id !== id)
    .map((node) => (isSection(node) ? { ...node, children: removeNodeById(node.children, id) } : node));

// Dot-decimal item numbering, same "N.0" top-level / "N.M" nested-child shape
// every other Bill Summary table's Item column uses (e.g. FoundationsBillSummary's
// `${index + 1}.0` main-header numbering) — one number per sibling, regardless of
// whether that sibling is a section or a leaf item, and nested sections extend
// their parent's number instead of restarting from 1.
const collectItemNumbers = (
  nodes: DynamicBillSummaryNode[],
  prefix: string,
  depth: number,
  out: Map<string, { label: string; depth: number }>,
) => {
  nodes.forEach((node, i) => {
    const label = prefix ? `${prefix}.${i + 1}` : `${i + 1}.0`;
    out.set(node.id, { label, depth });
    if (isSection(node)) {
      collectItemNumbers(node.children, prefix ? `${prefix}.${i + 1}` : `${i + 1}`, depth + 1, out);
    }
  });
};

const collectAllIds = (nodes: DynamicBillSummaryNode[], out: React.Key[]) => {
  nodes.forEach((node) => {
    out.push(node.id);
    if (isSection(node)) collectAllIds(node.children, out);
  });
};

interface DynamicBillSummaryPanelProps {
  tab: DynamicBillSummaryTab;
  onChange: (updated: DynamicBillSummaryTab) => void;
  isViewMode?: boolean;
  itemLibraryData?: ItemLibraryItem[];
  /** Live Plot Matrix columns (generatePlotMatrixCategoryColumns on the
   *  current groundworks data) — only used in dev mode, to drive the
   *  "Include in Plot Matrix" Valuation Stage / Stage lookups with exactly
   *  the columns Plot Matrix itself will generate (never a stage with no
   *  real column). */
  plotMatrixColumns?: PlotMatrixCategoryColumn[];
  /** Whether to offer the "Run Matrix Mapping" section — the Roads & Sewers
   *  counterpart of `plotMatrixColumns` above. Unlike Plot Matrix's stages,
   *  Run Matrix's section/column catalog is static (see
   *  RUN_MATRIX_MAPPING_SECTIONS in dynamicBillSummaryRunMatrix.ts), so this
   *  is just an on/off flag rather than a live-derived list. Omit (or leave
   *  false) to hide that section entirely — e.g. on /groundworks/review, which
   *  has Plot Matrix instead. */
  enableRunMatrixMapping?: boolean;
  /** Persisted line items for this tab (from the same boq-versions API every
   *  other Bill Summary's SummaryKey column reads), keyed by node id —
   *  drives the dev-mode-only SummaryKey column comparing the saved amount
   *  against the live-recomputed one. */
  apiLineItemsById?: Map<string, DynamicBillSummaryLineItem>;
  /** OH&P/Preliminaries markup — `(1 + overheadAndProfit/100) * (1 + preliminaries/100)`,
   *  same formula every fixed Bill Summary already applies to its own Rate/Amount
   *  columns. Applied here to this tab's own Rate/Amount columns and section/tab
   *  totals so a Dynamic Bill Summary item is marked up exactly like a fixed one.
   *  Defaults to 1 (no markup) when omitted. */
  markupFactor?: number;
  /** Whether adding/editing is currently allowed — see `useCanConfigureBoq()`
   *  (hooks/useCanConfigureBoq.ts), currently backed by the runtime
   *  "Configure BOQ" switch next to the Re-calculate button on
   *  /groundworks/review. When false, behaves the same as `isViewMode`:
   *  hides the Actions column and the root-level "Add Section"/"Add Item"
   *  buttons, and renders the Qty/Unit/Rate cells of already-added rows as
   *  read-only text instead of an editable input/select. Defaults to true
   *  (unaffected) when omitted. */
  configureBoqEnabled?: boolean;
}

/**
 * Renders one Dynamic Bill Summary tab's content: a nested Section/Item tree
 * (antd Table with tree data), an "Add Item" modal (item-library code lookup
 * + description + unit), and inline section/item CRUD. Reusable anywhere a
 * Dynamic Bill Summary tab is rendered — see `useDynamicBillSummary` for the
 * tab-level (add/rename/delete tab) state this pairs with.
 */
const DynamicBillSummaryPanel: React.FC<DynamicBillSummaryPanelProps> = ({
  tab,
  onChange,
  isViewMode,
  itemLibraryData,
  plotMatrixColumns = [],
  enableRunMatrixMapping = false,
  apiLineItemsById,
  markupFactor = 1,
  configureBoqEnabled = true,
}) => {
  const showDevMode = useDevMode();
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<DynamicBillSummaryLineItem | null>(null);

  // Code -> dbRate lookup straight off the item library, for the SummaryKey
  // modal's "BCCalculation" line — same convention every fixed Bill Summary's
  // own codeRateLookup uses (FoundationsBillSummary.tsx etc).
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, number>();
    (itemLibraryData ?? []).forEach((li) => {
      const code = li.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, li.nettRate || li.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getDbRate = (code: string) => codeRateLookup.get(code.trim().toUpperCase()) ?? 0;
  // Valuation Stage options — the same collapsed stage names Plot Matrix's
  // own Stage View lists (Site Strip, Cut/Fill, Foundation Exc & Conc, Plot
  // Drainage, ...), derived from the LIVE Plot Matrix columns
  // (generatePlotMatrixCategoryColumns), not a static category map, so a
  // selected stage always corresponds to a real, currently-generated column.
  const plotMatrixStageNames = useMemo(() => getPlotMatrixStageNames(plotMatrixColumns), [plotMatrixColumns]);

  // Every unit used anywhere in the app: the canonical display forms from
  // `UNIT_MAP` (constants.ts — the same normalisation every other table's
  // Unit column already goes through via `formatUnit`), unioned with whatever
  // units the live item library actually carries, so a code with a unit
  // outside that static map still shows up rather than being silently missing.
  const unitOptions = useMemo(() => {
    const set = new Set<string>(Object.values(UNIT_MAP));
    (itemLibraryData ?? []).forEach((li) => {
      if (li.unit) set.add(formatUnit(li.unit));
    });
    return Array.from(set).sort();
  }, [itemLibraryData]);
  const itemNumbers = useMemo(() => {
    const map = new Map<string, { label: string; depth: number }>();
    collectItemNumbers(tab.nodes, "", 0, map);
    return map;
  }, [tab.nodes]);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>(() => {
    const ids: React.Key[] = [];
    collectAllIds(tab.nodes, ids);
    return ids;
  });

  // Newly-added row (Section or Item) — briefly highlighted (CSS transition,
  // see the `.dynamic-bill-summary-table` <style> block below) so it's easy
  // to spot after its parent auto-expands. For a newly-added Number-type
  // item specifically, also drives autoFocus on its inline Qty cell (see the
  // Qty column below) — there's no Quantity field in the Add Item modal
  // itself, so the user's very next keystroke should land straight in it.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  // "Add/Edit Section" modal — same name-first flow useBillSummaryExtraItems.tsx's
  // Add/Edit Section modal uses (Cut & Fill Bill Summary's extra rows), instead of
  // creating a blank "New Section" node (or an existing one's label) inline-edited
  // in place. `editingSectionId` set = Edit Section; null = Add Section.
  const [sectionModalParentId, setSectionModalParentId] = useState<string | null>(null);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [sectionLabelDraft, setSectionLabelDraft] = useState("");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  const openAddSectionModal = (parentId: string | null) => {
    setSectionModalParentId(parentId);
    setSectionLabelDraft("");
    setEditingSectionId(null);
    setIsAddSectionModalOpen(true);
  };

  const openEditSectionModal = (node: DynamicBillSummarySectionNode) => {
    setSectionModalParentId(null);
    setSectionLabelDraft(node.label);
    setEditingSectionId(node.id);
    setIsAddSectionModalOpen(true);
  };

  const closeAddSectionModal = () => {
    setIsAddSectionModalOpen(false);
    setEditingSectionId(null);
  };

  // The sibling array a new section (or an edited one) actually sits in —
  // `tab.nodes` itself for a root-level section, otherwise the matching
  // parent section's own `children`. Used only for the duplicate-name check
  // below; `openEditSectionModal` doesn't track the real parent (it patches
  // the node in place regardless), so editing looks the node's own current
  // siblings up fresh via `findSiblingsOf` instead of `sectionModalParentId`.
  const findChildrenOfParent = (nodes: DynamicBillSummaryNode[], parentId: string): DynamicBillSummaryNode[] | null => {
    for (const node of nodes) {
      if (node.id === parentId && isSection(node)) return node.children;
      if (isSection(node)) {
        const found = findChildrenOfParent(node.children, parentId);
        if (found) return found;
      }
    }
    return null;
  };
  const findSiblingsOf = (nodes: DynamicBillSummaryNode[], targetId: string): DynamicBillSummaryNode[] | null => {
    if (nodes.some((n) => n.id === targetId)) return nodes;
    for (const node of nodes) {
      if (isSection(node)) {
        const found = findSiblingsOf(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Case-insensitive, trimmed duplicate-name check against sibling sections
  // only (same parent, or root level) — a section elsewhere in the tree may
  // reuse a name freely, only two siblings sharing one would be confusing.
  const isDuplicateSectionLabel = (label: string): boolean => {
    const normalized = label.trim().toLowerCase();
    const siblings = editingSectionId
      ? findSiblingsOf(tab.nodes, editingSectionId) ?? tab.nodes
      : sectionModalParentId === null
        ? tab.nodes
        : findChildrenOfParent(tab.nodes, sectionModalParentId) ?? [];
    return siblings.some(
      (n) => n.id !== editingSectionId && isSection(n) && n.label.trim().toLowerCase() === normalized,
    );
  };

  const confirmAddSection = () => {
    const label = sectionLabelDraft.trim();
    if (!label) return;
    if (isDuplicateSectionLabel(label)) {
      message.warning(`A section named "${label}" already exists here.`);
      return;
    }
    if (editingSectionId) {
      patchSection(editingSectionId, { label });
      closeAddSectionModal();
      return;
    }
    const newNode: DynamicBillSummaryNode = {
      id: genNodeId(),
      type: "section",
      label,
      children: [],
    };
    const parentId = sectionModalParentId;
    setExpandedRowKeys((prev) => (parentId && !prev.includes(parentId) ? [...prev, parentId] : prev));
    if (parentId === null) {
      onChange({ ...tab, nodes: [...tab.nodes, newNode] });
    } else {
      onChange({
        ...tab,
        nodes: mapNodes(tab.nodes, parentId, (node) =>
          isSection(node) ? { ...node, children: [...node.children, newNode] } : node,
        ),
      });
    }
    setNewlyAddedRowId(newNode.id);
    closeAddSectionModal();
  };

  const removeNode = (id: string) => {
    onChange({ ...tab, nodes: removeNodeById(tab.nodes, id) });
  };

  // Row delete confirmation — same DeleteConfirmationModal every other table
  // in the app uses (Garden.tsx, CutFills.tsx, etc), instead of a Popconfirm.
  const [deleteNodeId, setDeleteNodeId] = useState<string | null>(null);

  const patchSection = (id: string, patch: Partial<DynamicBillSummarySectionNode>) => {
    onChange({
      ...tab,
      nodes: mapNodes(tab.nodes, id, (node) => (isSection(node) ? { ...node, ...patch } : node)),
    });
  };

  const patchItem = (id: string, patch: Partial<DynamicBillSummaryItemNode>) => {
    onChange({
      ...tab,
      nodes: mapNodes(tab.nodes, id, (node) => (isSection(node) ? node : { ...node, ...patch })),
    });
  };

  // "Add Item" / "Edit Item" modal — shared between clicking any "Add Item"
  // control (root or inside a section, adds a fresh row) and clicking an
  // existing item's Edit icon (populates the same fields, saves in place
  // instead of appending). The item-library code lookup, Description, and
  // Unit are filled in together here; the code side is optional (leave the
  // search empty / don't pick a match for a plain manual item).
  const [addItemParentId, setAddItemParentId] = useState<string | null | undefined>(undefined);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [newItemDescription, setNewItemDescription] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("");
  const [newItemSearchText, setNewItemSearchText] = useState("");
  const [newItemSelectedCode, setNewItemSelectedCode] = useState<ItemLibraryItem | null>(null);
  // Amount Type — "number" (default) is the current qty/rate/code flow, with
  // the item-library code now mandatory. "text" drops qty/unit/rate/code and
  // Plot Matrix mapping entirely, in favour of a free-typed Amount value
  // (e.g. "Included", "Rate Only").
  const [newItemAmountType, setNewItemAmountType] = useState<"number" | "text">("number");
  const [newItemAmountText, setNewItemAmountText] = useState("");

  // Code Mode — "single" (default) is one code driving Rate directly.
  // "multiple" combines 2+ codes into one Rate via a per-entry +/- operator
  // (the first entry always seeds the running total; its own operator is
  // ignored). Only meaningful for amountType "number".
  const [newItemCodeMode, setNewItemCodeMode] = useState<"single" | "multiple">("single");
  const [newItemCodes, setNewItemCodes] = useState<Array<{ item: ItemLibraryItem; operator: "+" | "-" }>>([]);

  // Rounded to 2dp (same convention `computeBcCalculation`/`computeDynamicBcCalculation`
  // round dbRates to before multiplying by quantity) — an unrounded combined rate here
  // otherwise drifted from the SummaryKey modal's BCCalculation amount even when the
  // underlying dbRates matched, since qty × raw-rate ≠ qty × rounded-rate.
  const computeCombinedRate = (codes: Array<{ item: ItemLibraryItem; operator: "+" | "-" }>): number =>
    parseFloat(
      codes
        .reduce((acc, entry, i) => {
          if (i === 0) return entry.item.nettRate;
          return entry.operator === "-" ? acc - entry.item.nettRate : acc + entry.item.nettRate;
        }, 0)
        .toFixed(2),
    );

  const addMultiCode = (li: ItemLibraryItem) => {
    if (newItemCodes.some((entry) => entry.item.resourceCode === li.resourceCode)) {
      message.warning(`Code "${li.resourceCode}" has already been added.`);
      return;
    }
    setNewItemCodes((prev) => {
      const next = [...prev, { item: li, operator: "+" as const }];
      if (prev.length === 0) setNewItemUnit(formatUnit(li.unit) || "");
      return next;
    });
    setNewItemSearchText("");
  };

  const removeMultiCode = (index: number) => {
    setNewItemCodes((prev) => prev.filter((_, i) => i !== index));
  };

  const setMultiCodeOperator = (index: number, operator: "+" | "-") => {
    setNewItemCodes((prev) => prev.map((entry, i) => (i === index ? { ...entry, operator } : entry)));
  };

  // Optional Plot Matrix mapping — only surfaced in the modal in dev mode.
  // Off by default per item.
  // Plots/Garages are plain whole-group toggles — checking one applies the
  // item to every row of that type, no further per-plot/garage picker.
  const [plotMatrixEnabled, setPlotMatrixEnabled] = useState(false);
  const [plotMatrixStage, setPlotMatrixStage] = useState<string | undefined>(undefined);
  const [plotMatrixCategoryName, setPlotMatrixCategoryName] = useState<string | undefined>(undefined);
  const [plotMatrixDistributeTo, setPlotMatrixDistributeTo] = useState<Array<"plots" | "garages">>([]);

  const isAddItemModalOpen = addItemParentId !== undefined || editingItemId !== null;

  // Stage (sub-stage) options for the selected Valuation Stage — e.g. under
  // "Foundation Exc & Conc": "Excavate Foundation", "Pile Crop", "Concrete
  // Blinding", "Heave Protection", "Place Steel/Mesh", "Formwork",
  // "Concrete", "Abnormals". A Valuation Stage with only one column (no " - "
  // sub-split) yields a single Stage option equal to the stage name itself.
  const plotMatrixStageColumns = useMemo(
    () => (plotMatrixStage ? findPlotMatrixColumnsForStage(plotMatrixColumns, plotMatrixStage) : []),
    [plotMatrixColumns, plotMatrixStage],
  );

  // Auto-select when a Valuation Stage collapses to just one real column —
  // no point making the user pick a Stage with a single possible answer.
  useEffect(() => {
    if (plotMatrixStageColumns.length === 1 && !plotMatrixCategoryName) {
      setPlotMatrixCategoryName(plotMatrixStageColumns[0].categoryName);
    }
  }, [plotMatrixStageColumns, plotMatrixCategoryName]);

  const resetPlotMapping = () => {
    // Shown open by default for every Number-type item — same convention
    // Cut & Fill's own extra items modal uses (useBillSummaryExtraItems.tsx),
    // so the mapping form is visible the moment "Add Item" is clicked instead
    // of hiding behind an extra checkbox click — still fully skippable
    // (leave the stage unselected) for a plain item with no Plot Matrix
    // mapping. Independent of Dev Mode.
    setPlotMatrixEnabled(true);
    setPlotMatrixStage(undefined);
    setPlotMatrixCategoryName(undefined);
    setPlotMatrixDistributeTo([]);
  };

  // Optional Run Matrix mapping — the Roads & Sewers counterpart of Plot
  // Matrix mapping above: a Section ("tab") dropdown plus a Column ("child")
  // dropdown cascading off it, same shape as the Valuation Stage/Stage
  // cascade. Only surfaced when `enableRunMatrixMapping`.
  const [runMatrixMappingEnabled, setRunMatrixMappingEnabled] = useState(false);
  const [runMatrixSection, setRunMatrixSection] = useState<RunMatrixMappingSectionKey | undefined>(undefined);
  const [runMatrixColumnKey, setRunMatrixColumnKey] = useState<string | undefined>(undefined);

  const runMatrixSectionOptions = useMemo(() => getRunMatrixMappingSectionLabels(), []);
  const runMatrixSectionColumns = useMemo(
    () => findRunMatrixMappingColumnsForSection(runMatrixSection),
    [runMatrixSection],
  );

  useEffect(() => {
    if (runMatrixSectionColumns.length === 1 && !runMatrixColumnKey) {
      setRunMatrixColumnKey(runMatrixSectionColumns[0].key);
    }
  }, [runMatrixSectionColumns, runMatrixColumnKey]);

  const resetRunMatrixMapping = () => {
    setRunMatrixMappingEnabled(true);
    setRunMatrixSection(undefined);
    setRunMatrixColumnKey(undefined);
  };

  const buildRunMatrixMapping = (): DynamicBillSummaryRunMatrixMapping | undefined => {
    if (newItemAmountType === "text") return undefined;
    if (!runMatrixMappingEnabled) return undefined;
    if (!runMatrixSection || !runMatrixColumnKey) return undefined;
    return { section: runMatrixSection, columnKey: runMatrixColumnKey };
  };

  const openAddItemModal = (parentId: string | null) => {
    setEditingItemId(null);
    setAddItemParentId(parentId);
    setNewItemDescription("");
    setNewItemUnit("");
    setNewItemSearchText("");
    setNewItemSelectedCode(null);
    setNewItemAmountType("number");
    setNewItemAmountText("");
    setNewItemCodeMode("single");
    setNewItemCodes([]);
    resetPlotMapping();
    resetRunMatrixMapping();
  };

  const openEditItemModal = (item: DynamicBillSummaryItemNode) => {
    setAddItemParentId(undefined);
    setEditingItemId(item.id);
    setNewItemDescription(item.description);
    setNewItemUnit(item.unit ?? "");
    setNewItemSearchText("");
    setNewItemAmountType(item.amountType === "text" ? "text" : "number");
    setNewItemAmountText(item.amountText ?? "");
    const stubLibraryItem = (resourceCode: string, rate: number): ItemLibraryItem => ({
      region: "",
      libraryType: "",
      resourceCode,
      resourceDescription: item.description,
      unit: item.unit ?? "",
      baseRate: rate,
      nettRate: rate,
      resourceGroup: "",
      resourceType: "",
      costCodeCode: "",
      costCodeDescription: "",
    });
    if (item.resourceCodes && item.resourceCodes.length > 0) {
      setNewItemCodeMode("multiple");
      setNewItemCodes(
        item.resourceCodes.map((entry) => ({
          item:
            itemLibraryData?.find((li) => li.resourceCode === entry.resourceCode) ??
            stubLibraryItem(entry.resourceCode, 0),
          operator: entry.operator,
        })),
      );
      setNewItemSelectedCode(null);
    } else if (item.resourceCode) {
      setNewItemCodeMode("single");
      setNewItemCodes([]);
      const libraryMatch = itemLibraryData?.find((li) => li.resourceCode === item.resourceCode);
      setNewItemSelectedCode(libraryMatch ?? stubLibraryItem(item.resourceCode, item.rate ?? 0));
    } else {
      setNewItemCodeMode("single");
      setNewItemCodes([]);
      setNewItemSelectedCode(null);
    }
    if (item.plotMapping) {
      setPlotMatrixEnabled(true);
      setPlotMatrixStage(item.plotMapping.stage);
      setPlotMatrixCategoryName(item.plotMapping.categoryName);
      setPlotMatrixDistributeTo(item.plotMapping.distributeTo);
    } else {
      resetPlotMapping();
    }
    if (item.runMatrixMapping) {
      setRunMatrixMappingEnabled(true);
      setRunMatrixSection(item.runMatrixMapping.section as RunMatrixMappingSectionKey);
      setRunMatrixColumnKey(item.runMatrixMapping.columnKey);
    } else {
      resetRunMatrixMapping();
    }
  };

  const closeAddItemModal = () => {
    setAddItemParentId(undefined);
    setEditingItemId(null);
  };

  const buildPlotMapping = (): DynamicBillSummaryPlotMapping | undefined => {
    if (newItemAmountType === "text") return undefined;
    if (!plotMatrixEnabled) return undefined;
    if (!plotMatrixStage || !plotMatrixCategoryName || plotMatrixDistributeTo.length === 0) return undefined;
    return {
      stage: plotMatrixStage,
      categoryName: plotMatrixCategoryName,
      distributeTo: plotMatrixDistributeTo,
    };
  };

  const hasRequiredCode = () =>
    newItemCodeMode === "multiple" ? newItemCodes.length > 0 : !!newItemSelectedCode;

  // Single-code duplicate check — against sibling items only (same parent
  // section, or root), same scope `isDuplicateSectionLabel` checks names
  // against. A code already used elsewhere in the tree, or combined into
  // another item's "Multiple" list, is unaffected — only a second item in
  // this same section directly linked to the identical single code is
  // blocked, since that would double-count the same rate/cost line.
  const isDuplicateItemCode = (resourceCode: string): boolean => {
    const siblings = editingItemId
      ? findSiblingsOf(tab.nodes, editingItemId) ?? tab.nodes
      : (addItemParentId ?? null) === null
        ? tab.nodes
        : findChildrenOfParent(tab.nodes, addItemParentId as string) ?? [];
    return siblings.some((n) => n.id !== editingItemId && !isSection(n) && n.resourceCode === resourceCode);
  };

  // Case-insensitive, trimmed duplicate-name check against sibling items
  // only (same parent section, or root), same scope `isDuplicateItemCode`/
  // `isDuplicateSectionLabel` check against. An item elsewhere in the tree
  // may reuse a description freely — only two items in this same section
  // sharing one would be confusing/ambiguous about which line a rate/qty
  // belongs to.
  const isDuplicateItemDescription = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    const siblings = editingItemId
      ? findSiblingsOf(tab.nodes, editingItemId) ?? tab.nodes
      : (addItemParentId ?? null) === null
        ? tab.nodes
        : findChildrenOfParent(tab.nodes, addItemParentId as string) ?? [];
    return siblings.some(
      (n) => n.id !== editingItemId && !isSection(n) && n.description.trim().toLowerCase() === normalized,
    );
  };

  const confirmAddItem = () => {
    const description = newItemDescription.trim();
    if (!description) return;
    if (isDuplicateItemDescription(description)) {
      message.warning(`An item named "${description}" already exists in this section.`);
      return;
    }
    if (newItemAmountType === "number" && !hasRequiredCode()) return;
    if (newItemAmountType === "text" && !newItemAmountText.trim()) return;
    if (
      newItemAmountType === "number" &&
      newItemCodeMode === "single" &&
      newItemSelectedCode &&
      isDuplicateItemCode(newItemSelectedCode.resourceCode)
    ) {
      message.warning(`Code "${newItemSelectedCode.resourceCode}" already exists in this section.`);
      return;
    }
    const plotMapping = buildPlotMapping();
    const runMatrixMapping = buildRunMatrixMapping();

    const numberFields =
      newItemCodeMode === "multiple"
        ? {
            unit: newItemUnit.trim() || undefined,
            resourceCode: undefined,
            resourceCodes: newItemCodes.map(
              (entry, i): DynamicBillSummaryResourceCodeEntry => ({
                resourceCode: entry.item.resourceCode,
                operator: i === 0 ? "+" : entry.operator,
              }),
            ),
            rate: computeCombinedRate(newItemCodes),
            plotMapping,
            runMatrixMapping,
          }
        : {
            unit: newItemUnit.trim() || undefined,
            resourceCode: newItemSelectedCode?.resourceCode,
            resourceCodes: undefined,
            // Rounded to 2dp — same reasoning as `computeCombinedRate` above.
            rate: newItemSelectedCode ? parseFloat(newItemSelectedCode.nettRate.toFixed(2)) : undefined,
            plotMapping,
            runMatrixMapping,
          };

    if (editingItemId) {
      patchItem(
        editingItemId,
        newItemAmountType === "text"
          ? {
              description,
              amountType: "text",
              amountText: newItemAmountText.trim(),
              unit: undefined,
              qty: undefined,
              rate: undefined,
              resourceCode: undefined,
              resourceCodes: undefined,
              plotMapping: undefined,
              runMatrixMapping: undefined,
            }
          : {
              description,
              amountType: "number",
              amountText: undefined,
              ...numberFields,
            },
      );
      closeAddItemModal();
      return;
    }

    const newNode: DynamicBillSummaryNode =
      newItemAmountType === "text"
        ? {
            id: genNodeId(),
            type: "item",
            description,
            amountType: "text",
            amountText: newItemAmountText.trim(),
          }
        : {
            id: genNodeId(),
            type: "item",
            description,
            amountType: "number",
            unit: numberFields.unit,
            rate: numberFields.rate,
            ...(numberFields.resourceCode && { resourceCode: numberFields.resourceCode }),
            ...(numberFields.resourceCodes && { resourceCodes: numberFields.resourceCodes }),
            ...(plotMapping && { plotMapping }),
            ...(runMatrixMapping && { runMatrixMapping }),
          };
    const parentId = addItemParentId ?? null;
    setExpandedRowKeys((prev) => (parentId && !prev.includes(parentId) ? [...prev, parentId] : prev));
    if (parentId === null) {
      onChange({ ...tab, nodes: [...tab.nodes, newNode] });
    } else {
      onChange({
        ...tab,
        nodes: mapNodes(tab.nodes, parentId, (node) =>
          isSection(node) ? { ...node, children: [...node.children, newNode] } : node,
        ),
      });
    }
    setNewlyAddedRowId(newNode.id);
    closeAddItemModal();
  };

  const filteredLibraryItems = useMemo(() => {
    const query = newItemSearchText.trim().toLowerCase();
    if (!itemLibraryData || !query) return [];
    return itemLibraryData
      .filter(
        (li) =>
          li.resourceDescription?.toLowerCase().includes(query) ||
          li.resourceCode?.toLowerCase().includes(query),
      )
      .slice(0, 30);
  }, [itemLibraryData, newItemSearchText]);

  const columns: ColumnsType<DynamicBillSummaryNode> = [
    {
      title: "Item",
      key: "item",
      // "1%" (rather than a numeric px width) is the standard antd/rc-table
      // shrink-to-fit trick: under the table's default non-fixed layout, this
      // makes the browser allocate the column only as much space as its
      // (nowrap) content actually needs, instead of sharing leftover table
      // width with the unconstrained Description column — no magic px value
      // to keep in sync as nesting gets deeper.
      width: 140,
      align: "center",
      render: (_, record) => {
        const entry = itemNumbers.get(record.id);
        const label = <span style={{ whiteSpace: "nowrap" }}>{entry?.label}</span>;
        return isSection(record) ? <Text strong>{label}</Text> : label;
      },
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => {
        if (isSection(record)) {
          // Every section — regardless of nesting depth — shares one uppercase
          // bold style: the same indigo-600 "sub-header" row color every other
          // Bill Summary table uses (Foundations/Garden/Scaffold/etc — `text-indigo-600`,
          // no size bump), applied inline rather than via Tailwind className since
          // antd's own Text/Input styles otherwise win the color at equal specificity.
          // Always read-only — a section's name is set via the Add/Edit Section
          // modal, same as Cut & Fill Bill Summary's own extra items (no inline
          // table-cell editing).
          const sectionStyle: React.CSSProperties = {
            color: "#4f46e5",
            // textTransform: "uppercase",
          };
          return (
            <Text strong style={sectionStyle}>
              {record.label}
            </Text>
          );
        }
        return (
          <div>
            {/* Always read-only — an item's description is set via the
                Add/Edit Item modal, same as Cut & Fill Bill Summary's own
                extra items (no inline table-cell editing). */}
            <Text>{record.description}</Text>
            {showDevMode && record.resourceCode && (
              <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{record.resourceCode}</div>
            )}
            {showDevMode && record.resourceCodes && record.resourceCodes.length > 0 && (
              <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                {record.resourceCodes
                  .map((c, i) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`))
                  .join(" ")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Qty",
      key: "qty",
      width: 100,
      align: "right",
      render: (_, record) => {
        if (isSection(record) || record.amountType === "text") return "";
        return isViewMode || !configureBoqEnabled ? (
          record.qty ?? ""
        ) : (
          <NumberInput
            value={record.qty ?? 0}
            onChange={(value) => patchItem(record.id, { qty: value })}
            size="small"
            decimalPlaces={2}
            autoFocus={record.id === newlyAddedRowId}
          />
        );
      },
    },
    {
      title: "Unit",
      key: "unit",
      width: 90,
      align: "center",
      render: (_, record) => {
        if (isSection(record) || record.amountType === "text") return "";
        return isViewMode || !configureBoqEnabled ? (
          record.unit ?? ""
        ) : (
          <Select
            value={record.unit || undefined}
            onChange={(value) => patchItem(record.id, { unit: value })}
            options={unitOptions.map((u) => ({ value: u, label: u }))}
            placeholder="Unit"
            size="small"
            showSearch
            allowClear
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Rate",
      key: "rate",
      width: 110,
      align: "right",
      render: (_, record) => {
        if (isSection(record) || record.amountType === "text") return "";
        const displayRate = computeDynamicBillSummaryNodeRate(record.rate, markupFactor);
        if (isViewMode || !configureBoqEnabled) {
          return record.rate != null ? formatCurrency(displayRate) : "";
        }
        // Rate comes from the linked item-library code and isn't hand-editable
        // once one is assigned — same as every other Bill Summary table, whose
        // Rate column always reads straight off the item library, never a
        // free-typed value, whenever a resourceCode is present. Displayed
        // marked up (same as every fixed Bill Summary's own Rate column) —
        // when hand-editable (no linked code), the typed value is divided
        // back down by markupFactor before being stored, so the underlying
        // node keeps holding the raw rate everywhere else reads from.
        return (
          <NumberInput
            value={displayRate}
            onChange={(value) => patchItem(record.id, { rate: value / markupFactor })}
            size="small"
            decimalPlaces={2}
            disabled={!!record.resourceCode || !!record.resourceCodes?.length}
            title={
              record.resourceCode || record.resourceCodes?.length
                ? "Rate is set by the linked item library code(s)"
                : undefined
            }
          />
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      width: 130,
      align: "right",
      render: (_, record) => {
        // Section rows only surface their subtotal while collapsed — same
        // "show section total when collapsed, blank when expanded" convention
        // FoundationsBillSummary's Amount column uses, since an expanded
        // section's own children already show the same figures below it.
        if (isSection(record)) {
          if (expandedRowKeys.includes(record.id) || record.children.length === 0) return "";
          return <Text strong>{formatCurrency(computeDynamicBillSummaryNodeAmount(record, markupFactor))}</Text>;
        }
        if (record.amountType === "text") return <Text>{record.amountText}</Text>;
        return <Text>{formatCurrency(computeDynamicBillSummaryNodeAmount(record, markupFactor))}</Text>;
      },
    },
    ...(showDevMode
      ? [
          {
            title: "SummaryKey",
            key: "summaryKey",
            width: 150,
            align: "right" as const,
            render: (_: unknown, record: DynamicBillSummaryNode) => {
              if (isSection(record)) return "";
              // Falls back to a live-derived line item (same shape the item would
              // serialise to right now) whenever nothing's been saved for this node
              // yet — a freshly-added item otherwise showed a blank SummaryKey cell
              // until the next save/reload, even though it already has a real amount.
              const persisted = apiLineItemsById?.get(record.id);
              const isUnsaved = !persisted;
              const displayed = persisted ?? nodeToLineItem(record);
              // Deliberately raw (no markupFactor) — this compares against the
              // persisted amount, which is also raw (persistence never bakes
              // markup into a stored line, same as every fixed Bill Summary's
              // own persisted lines), so applying markup here would show a
              // false "unsaved/mismatch" on every marked-up item.
              const liveAmount = computeDynamicBillSummaryNodeAmount(record);
              // Backend-style dbRate-only derivation — same "BC: £..." sub-line every
              // fixed Bill Summary's SummaryKey column already shows below the
              // persisted amount (FoundationsBillSummary.tsx etc), computed here via
              // `computeDynamicBcCalculation` since text-amount / codeless items have
              // no BC line to derive.
              const bc = computeDynamicBcCalculation(displayed, getDbRate);
              const mismatch = bc ? Math.abs(liveAmount - bc.amount) > 0.01 : false;
              return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Text style={{ fontSize: 12, color: mismatch ? "#dc2626" : "#6b7280" }}>
                      {formatCurrency(displayed.amount || 0)}
                    </Text>
                    <InfoCircleOutlined
                      style={{ cursor: "pointer", color: "#3b82f6" }}
                      onClick={() => setSummaryKeyModalRecord(displayed)}
                    />
                  </div>
                  {bc && (
                    <Text style={{ fontSize: 10, color: mismatch ? "#dc2626" : "#3b82f6" }}>
                      BC: {formatCurrency(bc.amount)}
                    </Text>
                  )}
                  {isUnsaved && (
                    <Text style={{ fontSize: 10, color: "#f59e0b" }}>unsaved</Text>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    ...(isViewMode || !configureBoqEnabled
      ? []
      : [
          {
            title: "Actions",
            key: "actions",
            width: 150,
            align: "center" as const,
            render: (_: unknown, record: DynamicBillSummaryNode) => (
              <Space size={4}>
                {isSection(record) ? (
                  <>
                    {(() => {
                      const atMaxDepth = (itemNumbers.get(record.id)?.depth ?? 0) >= MAX_SECTION_NESTING_DEPTH;
                      const addSectionButton = (
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          onClick={() => openAddSectionModal(record.id)}
                          disabled={atMaxDepth}
                          title={atMaxDepth ? undefined : "Add Section"}
                        />
                      );
                      return atMaxDepth ? (
                        <Tooltip title={MAX_SECTION_NESTING_DEPTH_MESSAGE}>{addSectionButton}</Tooltip>
                      ) : (
                        addSectionButton
                      );
                    })()}
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => openAddItemModal(record.id)}
                      title="Add Item"
                    />
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openEditSectionModal(record)}
                      title="Edit Section"
                    />
                  </>
                ) : (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditItemModal(record)}
                    title="Edit Item"
                  />
                )}
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  title="Delete"
                  onClick={() => setDeleteNodeId(record.id)}
                />
              </Space>
            ),
          },
        ]),
  ];

  const total = tab.nodes.reduce((sum, node) => sum + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0);

  return (
    <div style={{ padding: "16px" }}>
      {/* Same "<name> Bill Summary" header + Expand All/Collapse All row every
          fixed Bill Summary table uses (FoundationsBillSummary.tsx etc) —
          ExpandCollapseAllButton itself scrolls to top on click. */}
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: "16px", color: "#111827" }}>
          {tab.label} Bill Summary
        </Title>
        <Space>
          {!isViewMode && configureBoqEnabled && (
            <>
              <Button size="small" icon={<FolderAddOutlined />} onClick={() => openAddSectionModal(null)}>
                Add Section
              </Button>
              {/* <Button size="small" icon={<PlusOutlined />} onClick={() => openAddItemModal(null)}>
                Add Item
              </Button> */}
            </>
          )}
          <ExpandCollapseAllButton
            expanded={expandedRowKeys.length > 0}
            onToggle={() => {
              if (expandedRowKeys.length > 0) {
                setExpandedRowKeys([]);
              } else {
                const ids: React.Key[] = [];
                collectAllIds(tab.nodes, ids);
                setExpandedRowKeys(ids);
              }
            }}
          />
        </Space>
      </div>
      {/* The Description cell grows taller than Qty/Unit/Rate/Amount whenever
          the dev-mode resourceCode sub-line renders beneath its Input — antd
          Table cells default to middle-aligned content, so those shorter
          cells' inputs would otherwise center within the taller row instead
          of lining up with Description's Input at the top. Force every cell
          to top-align so all inputs stay flush across a row regardless. */}
      <style>{`
        .dynamic-bill-summary-table .ant-table-tbody > tr > td {
          vertical-align: top;
        }
        .dynamic-bill-summary-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
        /* Newly-added Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes dynamic-bill-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .dynamic-bill-summary-row-added > td {
          animation: dynamic-bill-summary-row-added-fade 1.8s ease-out;
        }
        /* Smooth expand/collapse instead of antd's default instant toggle. */
        .dynamic-bill-summary-table .ant-table-row-expand-icon {
          transition: transform 0.2s ease;
        }
        .dynamic-bill-summary-table .ant-table-tbody > tr {
          transition: background-color 0.3s ease;
        }
      `}</style>
      <Table<DynamicBillSummaryNode>
        className="dynamic-bill-summary-table"
        rowKey="id"
        dataSource={tab.nodes}
        columns={columns}
        pagination={false}
        size="middle"
        bordered
        // Same table styling every fixed Bill Summary uses (e.g.
        // GroundworksCutFillBillSummary.tsx) — bordered, "middle" row
        // density, indigo-tinted section rows — instead of this panel's own
        // borderless "small" look.
        rowClassName={(record) => {
          const classes: string[] = [];
          if (isSection(record)) classes.push("bg-indigo-50", "font-semibold");
          if (record.id === newlyAddedRowId) classes.push("dynamic-bill-summary-row-added");
          return classes.join(" ");
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as React.Key[]),
        }}
        locale={{ emptyText: "No rows yet — add a section to get started." }}
        summary={() => (
          <Table.Summary fixed="bottom">
            {/* Same "TOTAL" row styling every fixed Bill Summary's footer uses
                (e.g. GroundworksCutFillBillSummary.tsx) — grey row background,
                bold label, right-aligned bold amount in a block span. */}
            <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={5}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL {tab.label.toUpperCase()}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <span style={{ display: "block", textAlign: "right", fontWeight: 700 }}>
                  {formatCurrency(total)}
                </span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={2} />}
              {!isViewMode && configureBoqEnabled && <Table.Summary.Cell index={showDevMode ? 3 : 2} />}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />

      <Modal
        title={editingItemId ? "Edit Item" : "Add Item"}
        open={isAddItemModalOpen}
        onCancel={closeAddItemModal}
        onOk={confirmAddItem}
        okText={editingItemId ? "Save" : "Add Item"}
        okButtonProps={{
          disabled:
            !newItemDescription.trim() ||
            isDuplicateItemDescription(newItemDescription) ||
            (newItemAmountType === "number" ? !hasRequiredCode() : !newItemAmountText.trim()) ||
            // Plot Matrix Mapping's own fields are marked required (*) once
            // "Include in Plot Matrix" is on — enforce that here too, not
            // just visually, same as Description/Item Library Code above.
            // Number-type only (matches buildPlotMapping's own amountType guard).
            // Gated on `plotMatrixColumns.length > 0` (same guard the section's
            // own render condition below uses) — `plotMatrixEnabled` defaults to
            // `true` on every Add Item open (resetPlotMapping), regardless of
            // whether this page even passed real Plot Matrix columns through
            // (e.g. /roads-sewers/review, which has Run Matrix instead and never
            // renders this section at all). Without this guard, a page with no
            // Plot Matrix columns permanently disabled Add/Save — the required
            // Stage/Category/Distribute-to fields could never be filled in
            // because the section that holds them was never on screen.
            (newItemAmountType === "number" &&
              plotMatrixColumns.length > 0 &&
              plotMatrixEnabled &&
              (!plotMatrixStage || !plotMatrixCategoryName || plotMatrixDistributeTo.length === 0)) ||
            // Run Matrix Mapping's own fields — same required-when-enabled
            // enforcement and the same "gated on the flag that also gates the
            // section's own render condition" reasoning as Plot Matrix Mapping
            // immediately above.
            (newItemAmountType === "number" &&
              enableRunMatrixMapping &&
              runMatrixMappingEnabled &&
              (!runMatrixSection || !runMatrixColumnKey)),
        }}
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Amount Type
          </Text>
          <Segmented
            value={newItemAmountType}
            onChange={(value) => setNewItemAmountType(value as "number" | "text")}
            options={[
              { label: "Number", value: "number" },
              { label: "Text", value: "text" },
            ]}
          />
        </div>

        {newItemAmountType === "text" ? (
          <>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Description <Text type="danger">*</Text>
          </Text>
          <TextInput
            placeholder="Item description"
            value={newItemDescription}
            onChange={setNewItemDescription}
            maxLength={100}
            status={isDuplicateItemDescription(newItemDescription) ? "error" : undefined}
          />
          {isDuplicateItemDescription(newItemDescription) && (
            <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
              An item with this name already exists in this section.
            </Text>
          )}
        </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              Amount (Text)
            </Text>
            <TextInput
              placeholder="e.g. Included, Rate Only"
              value={newItemAmountText}
              onChange={setNewItemAmountText}
              textOnly
              maxLength={100}
            />
          </div>
          </>
        ) : (
        <>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Item Library Code <Text type="danger">*</Text>
          </Text>
          <Segmented
            value={newItemCodeMode}
            onChange={(value) => {
              setNewItemCodeMode(value as "single" | "multiple");
              setNewItemSearchText("");
            }}
            options={[
              { label: "Single", value: "single" },
              { label: "Multiple", value: "multiple" },
            ]}
            size="small"
            style={{ marginBottom: 8 }}
          />

          {newItemCodeMode === "single" ? (
            newItemSelectedCode ? (
              <Tag
                closable
                closeIcon={<CloseCircleFilled />}
                onClose={() => setNewItemSelectedCode(null)}
                color="blue"
                style={{ padding: "4px 8px", fontSize: 12 }}
              >
                {newItemSelectedCode.resourceCode} — {newItemSelectedCode.resourceDescription} (
                {formatCurrency(newItemSelectedCode.nettRate)})
              </Tag>
            ) : (
              <>
                <Input.Search
                  placeholder="Search by description or code"
                  value={newItemSearchText}
                  onChange={(e) => setNewItemSearchText(e.target.value)}
                  autoFocus
                />
                {newItemSearchText.trim() && (
                  <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
                    {filteredLibraryItems.length === 0 ? (
                      <div style={{ padding: 10 }}>
                        <Text type="secondary">No matches — refine your search or leave this blank to skip.</Text>
                      </div>
                    ) : (
                      filteredLibraryItems.map((li) => (
                        <div
                          key={li.resourceCode}
                          onClick={() => {
                            if (isDuplicateItemCode(li.resourceCode)) {
                              message.warning(`Code "${li.resourceCode}" already exists in this section.`);
                              return;
                            }
                            // Picking (or changing) a code always syncs Description/Unit to
                            // match it — the code is the source of truth once linked, same as
                            // every other Bill Summary row driven by an item-library code.
                            setNewItemSelectedCode(li);
                            setNewItemDescription(li.resourceDescription);
                            setNewItemUnit(formatUnit(li.unit) || "");
                          }}
                          style={{
                            padding: "8px 10px",
                            borderBottom: "1px solid #f0f0f0",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{li.resourceDescription}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {li.resourceCode} · {li.unit} · {formatCurrency(li.nettRate)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )
          ) : (
            <div>
              {newItemCodes.map((entry, i) => (
                <div
                  key={`${entry.item.resourceCode}_${i}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
                >
                  {i > 0 && (
                    <Select
                      value={entry.operator}
                      onChange={(value) => setMultiCodeOperator(i, value as "+" | "-")}
                      options={RESOURCE_CODE_OPERATOR_OPTIONS}
                      size="small"
                      style={{ width: 56 }}
                    />
                  )}
                  <Tag
                    closable
                    closeIcon={<CloseCircleFilled />}
                    onClose={() => removeMultiCode(i)}
                    color="blue"
                    style={{ padding: "4px 8px", fontSize: 12 }}
                  >
                    {entry.item.resourceCode} — {entry.item.resourceDescription} ({formatCurrency(entry.item.nettRate)})
                  </Tag>
                </div>
              ))}
              <Input.Search
                placeholder="Search by description or code to add"
                value={newItemSearchText}
                onChange={(e) => setNewItemSearchText(e.target.value)}
              />
              {newItemSearchText.trim() && (
                <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
                  {filteredLibraryItems.length === 0 ? (
                    <div style={{ padding: 10 }}>
                      <Text type="secondary">No matches — refine your search.</Text>
                    </div>
                  ) : (
                    filteredLibraryItems.map((li) => (
                      <div
                        key={li.resourceCode}
                        onClick={() => addMultiCode(li)}
                        style={{
                          padding: "8px 10px",
                          borderBottom: "1px solid #f0f0f0",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{li.resourceDescription}</div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {li.resourceCode} · {li.unit} · {formatCurrency(li.nettRate)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {newItemCodes.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text strong>Combined Rate: </Text>
                  <Text>{formatCurrency(computeCombinedRate(newItemCodes))}</Text>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Description <Text type="danger">*</Text>
          </Text>
          <TextInput
            placeholder="Item description"
            value={newItemDescription}
            onChange={setNewItemDescription}
            maxLength={100}
            status={isDuplicateItemDescription(newItemDescription) ? "error" : undefined}
          />
          {isDuplicateItemDescription(newItemDescription) && (
            <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
              An item with this name already exists in this section.
            </Text>
          )}
        </div>

        <div>
          <Text strong style={{ display: "block", marginBottom: 4 }}>
            Unit
          </Text>
          <Select
            value={newItemUnit || undefined}
            onChange={(value) => setNewItemUnit(value)}
            options={unitOptions.map((u) => ({ value: u, label: u }))}
            placeholder="Select a unit"
            showSearch
            allowClear
            onClear={() => setNewItemUnit("")}
            style={{ width: "100%" }}
          />
        </div>

        {plotMatrixColumns.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Divider style={{ margin: "0 0 12px" }}>Plot Matrix Mapping</Divider>

            {plotMatrixEnabled && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Valuation Stage <Text type="danger">*</Text>
                </Text>
                <Select
                  value={plotMatrixStage}
                  onChange={(value) => {
                    setPlotMatrixStage(value);
                    setPlotMatrixCategoryName(undefined);
                    if (!isGarageEligibleValuationStage(value)) {
                      setPlotMatrixDistributeTo((prev) => prev.filter((v) => v !== "garages"));
                    }
                  }}
                  options={plotMatrixStageNames.map((stage) => ({ value: stage, label: stage }))}
                  placeholder="Select a valuation stage"
                  showSearch
                  style={{ width: "100%", marginBottom: 12 }}
                />

                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Stage <Text type="danger">*</Text>
                </Text>
                <Select
                  value={plotMatrixCategoryName}
                  onChange={setPlotMatrixCategoryName}
                  options={plotMatrixStageColumns.map((c) => ({
                    value: c.categoryName,
                    label: getPlotMatrixSubStageLabel(c.categoryName),
                  }))}
                  placeholder={plotMatrixStage ? "Select a stage" : "Select a valuation stage first"}
                  disabled={!plotMatrixStage}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%", marginBottom: 12 }}
                />

                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Distribute amount across <Text type="danger">*</Text>
                </Text>
                <Space>
                  <Checkbox
                    checked={plotMatrixDistributeTo.includes("plots")}
                    onChange={(e) =>
                      setPlotMatrixDistributeTo((prev) =>
                        e.target.checked ? [...prev, "plots"] : prev.filter((v) => v !== "plots"),
                      )
                    }
                  >
                    Plots
                  </Checkbox>
                  {(!plotMatrixStage || isGarageEligibleValuationStage(plotMatrixStage)) && (
                    <Checkbox
                      checked={plotMatrixDistributeTo.includes("garages")}
                      onChange={(e) =>
                        setPlotMatrixDistributeTo((prev) =>
                          e.target.checked ? [...prev, "garages"] : prev.filter((v) => v !== "garages"),
                        )
                      }
                    >
                      Garages
                    </Checkbox>
                  )}
                </Space>
              </div>
            )}
          </div>
        )}

        {enableRunMatrixMapping && (
          <div style={{ marginTop: 16 }}>
            <Divider style={{ margin: "0 0 12px" }}>Run Matrix Mapping</Divider>

            {runMatrixMappingEnabled && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Section <Text type="danger">*</Text>
                </Text>
                <Select
                  value={runMatrixSection}
                  onChange={(value) => {
                    setRunMatrixSection(value as RunMatrixMappingSectionKey);
                    setRunMatrixColumnKey(undefined);
                  }}
                  options={runMatrixSectionOptions.map((s) => ({ value: s.key, label: s.label }))}
                  placeholder="Select a Run Matrix section"
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%", marginBottom: 12 }}
                />

                <Text strong style={{ display: "block", marginBottom: 4 }}>
                  Column <Text type="danger">*</Text>
                </Text>
                <Select
                  value={runMatrixColumnKey}
                  onChange={setRunMatrixColumnKey}
                  options={runMatrixSectionColumns.map((c) => ({ value: c.key, label: c.label }))}
                  placeholder={runMatrixSection ? "Select a column" : "Select a section first"}
                  disabled={!runMatrixSection}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                />
              </div>
            )}
          </div>
        )}
        </>
        )}
      </Modal>

      <Modal
        title={editingSectionId ? "Edit Section" : "Add Section"}
        open={isAddSectionModalOpen}
        onCancel={closeAddSectionModal}
        onOk={confirmAddSection}
        okText={editingSectionId ? "Save" : "Add Section"}
        okButtonProps={{
          disabled: !sectionLabelDraft.trim() || isDuplicateSectionLabel(sectionLabelDraft),
        }}
        destroyOnClose
      >
        <Text strong style={{ display: "block", marginBottom: 4 }}>
          Section Name
        </Text>
        <TextInput
          placeholder="Section name"
          value={sectionLabelDraft}
          onChange={setSectionLabelDraft}
          onPressEnter={confirmAddSection}
          maxLength={100}
          status={isDuplicateSectionLabel(sectionLabelDraft) ? "error" : undefined}
          autoFocus
        />
        {isDuplicateSectionLabel(sectionLabelDraft) && (
          <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
            A section with this name already exists here.
          </Text>
        )}
      </Modal>

      <Modal
        title="SummaryKey — Persisted Values"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <SummaryKeyRawFields record={summaryKeyModalRecord as unknown as Record<string, unknown>} />
        )}
        {summaryKeyModalRecord && (() => {
          const bc = computeDynamicBcCalculation(summaryKeyModalRecord, getDbRate);
          if (!bc) return null;
          return (
            <div style={{ marginTop: 8, padding: 8, background: "#eff6ff", borderRadius: 4 }}>
              <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
              <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                <Descriptions.Item label="Computed Rate (rounded 2dp)">{formatCurrency(bc.rate)}</Descriptions.Item>
                <Descriptions.Item label="Quantity (rounded)">
                  {bc.quantity.toLocaleString("en-GB")} {formatUnit(summaryKeyModalRecord.unit)}
                </Descriptions.Item>
                <Descriptions.Item label="Amount (rounded 2dp)">{formatCurrency(bc.amount)}</Descriptions.Item>
              </Descriptions>
              <div style={{ marginTop: 8 }}>
                {bc.steps.map((step, idx) => (
                  <div key={idx}>
                    <Text style={{ fontSize: 12, color: "#374151" }}>{step}</Text>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>

      <DeleteConfirmationModal
        visible={deleteNodeId !== null}
        onConfirm={() => {
          if (deleteNodeId) removeNode(deleteNodeId);
          setDeleteNodeId(null);
        }}
        onCancel={() => setDeleteNodeId(null)}
        title="Delete this row and its contents?"
      />
    </div>
  );
};

export default DynamicBillSummaryPanel;
